import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

interface Device {
  id: number;
  name: string;
  active: boolean;
}

const DevicePanel: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([
    { id: 1, name: "Smart Watch", active: true },
    { id: 2, name: "Apple Watch Series 8", active: false },
  ]);
  const [open, setOpen] = useState(false);
  const [deviceType, setDeviceType] = useState("");
  const [modelName, setModelName] = useState("");

  const handleAddDevice = () => setOpen(true);

  const handleSaveDevice = () => {
    if (!deviceType || !modelName) return;
    const newDevice: Device = {
      id: devices.length + 1,
      name: `${deviceType} - ${modelName}`,
      active: false,
    };
    setDevices([...devices, newDevice]);
    setDeviceType("");
    setModelName("");
    setOpen(false);
  };

  const handleRemoveDevice = (id: number) => {
    setDevices(devices.filter((d) => d.id !== id));
  };

  const handleSelectDevice = (id: number) => {
    setDevices((prev) =>
      prev.map((d) => ({ ...d, active: d.id === id }))
    );
  };

  return (
    <>
      {/* Main Layout */}
      <Box display="flex" p={2}>
        {/* Left Panel */}
        <Box width="25%" pr={2} borderRight="1px solid #ddd">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleAddDevice}
          >
            + Add Device
          </Button>

          <Box mt={2}>
            {devices.map((device) => (
              <Card
                key={device.id}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor: device.active ? "primary.main" : "#ddd",
                  cursor: "pointer",
                }}
                onClick={() => handleSelectDevice(device.id)}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                  }}
                >
                  <Typography>{device.name}</Typography>
                  {device.active && (
                    <Typography color="success.main" variant="body2">
                      Active
                    </Typography>
                  )}
                </CardContent>
                <Typography
                  variant="body2"
                  color="error"
                  textAlign="right"
                  pr={1}
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDevice(device.id);
                  }}
                >
                  Remove Device
                </Typography>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Right Panel */}
        <Box width="75%" pl={2}>
          <Typography variant="h6" mb={2}>
            Patient Vital Risk Thresholds
          </Typography>

          {/* --- Heart Rate Section --- */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>
                Heart Rate
              </Typography>

              <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
                <TextField label="Low threshold (bpm)" value={50} sx={{ flex: "1 1 200px" }} />
                <TextField label="High threshold (bpm)" value={120} sx={{ flex: "1 1 200px" }} />

                <FormControl sx={{ flex: "1 1 200px" }}>
                  <InputLabel>Frequency Type</InputLabel>
                  <Select defaultValue="hrs" label="Frequency Type">
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="hrs">Hours</MenuItem>
                    <MenuItem value="min">Minutes</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: "1 1 200px" }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select defaultValue={4} label="Frequency">
                    {[1, 2, 3, 4, 5, 6].map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">Alert To:</Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox defaultChecked />} label="Assigned Doctor" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Caretaker 1" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Caretaker 2" />
                <FormControlLabel control={<Checkbox />} label="Caretaker 3" />
              </FormGroup>

              <Typography variant="subtitle2" mt={2}>
                Alert Mode:
              </Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox defaultChecked />} label="App Notification" />
                <FormControlLabel control={<Checkbox />} label="Email" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Chat" />
              </FormGroup>
            </CardContent>
          </Card>

          {/* --- Blood Pressure Section --- */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>
                Blood Pressure
              </Typography>

              <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
                <TextField label="Systolic Min" value={90} sx={{ flex: "1 1 200px" }} />
                <TextField label="Systolic Max" value={140} sx={{ flex: "1 1 200px" }} />
                <TextField label="Diastolic Min" value={60} sx={{ flex: "1 1 200px" }} />
                <TextField label="Diastolic Max" value={90} sx={{ flex: "1 1 200px" }} />

                <FormControl sx={{ flex: "1 1 200px" }}>
                  <InputLabel>Frequency Type</InputLabel>
                  <Select defaultValue="hrs" label="Frequency Type">
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="hrs">Hours</MenuItem>
                    <MenuItem value="min">Minutes</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: "1 1 200px" }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select defaultValue={4} label="Frequency">
                    {[1, 2, 3, 4, 5, 6].map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">Alert To:</Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox defaultChecked />} label="Assigned Doctor" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Caretaker 1" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Caretaker 2" />
                <FormControlLabel control={<Checkbox />} label="Caretaker 3" />
              </FormGroup>

              <Typography variant="subtitle2" mt={2}>
                Alert Mode:
              </Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox defaultChecked />} label="App Notification" />
                <FormControlLabel control={<Checkbox />} label="Email" />
                <FormControlLabel control={<Checkbox defaultChecked />} label="Chat" />
              </FormGroup>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* --- Add Device Modal --- */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: 372, borderRadius: 2, p: 1 },
        }}
      >
        <DialogTitle sx={{ p: 1, display: "flex", justifyContent: "space-between" }}>
          <Typography fontWeight={600}>Add Device</Typography>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Device</InputLabel>
            <Select
              value={deviceType}
              label="Select Device"
              onChange={(e) => setDeviceType(e.target.value)}
            >
              <MenuItem value="Glucose Meter">Glucose Meter</MenuItem>
              <MenuItem value="Heart Rate Monitor">Heart Rate Monitor</MenuItem>
              <MenuItem value="Blood Pressure Monitor">Blood Pressure Monitor</MenuItem>
              <MenuItem value="Smart Watch">Smart Watch</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Model Name</InputLabel>
            <Select
              value={modelName}
              label="Model Name"
              onChange={(e) => setModelName(e.target.value)}
            >
              <MenuItem value="Dexcom G7">Dexcom G7</MenuItem>
              <MenuItem value="Omron Platinum">Omron Platinum</MenuItem>
              <MenuItem value="Fitbit Sense 2">Fitbit Sense 2</MenuItem>
              <MenuItem value="Apple Watch S8">Apple Watch S8</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            sx={{ borderRadius: 2 }}
            onClick={handleSaveDevice}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DevicePanel;
